import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import { Box } from "@mui/material";
import Styles from "../BusinessDescriptionForm.module.css";
import { apiService } from "../../../../api/apiService";

export type CRITICALTableData = {
  output: any;
  reputational_external_impact_on: any;
  monthly_financial_impact: any;
  compliance_or_external_regulatory_affairs_impact: any;
  time_to_deliver_the_service: any;
  sum: any;
  critical: any;
};

interface CriticalTableStepProps {
  outputs: string[];
  setCriticalTableData: React.Dispatch<
    React.SetStateAction<CRITICALTableData[]>
  >;
  criticalTableData: CRITICALTableData[];
  isCriticalTableFilled: boolean;
}

interface Rating {
  reputational_external_impact_on: number;
  stakeholders: number;
  compliance_or_external_regulatory_affairs_impact: number;
  peopleRoles: number;
  sum: number;
}

interface ApiResponse {
  output: string;
  reputational_external_impact_on: number;
  monthly_financial_impact: number;
  compliance_or_external_regulatory_affairs_impact: number;
  time_to_deliver_the_service: number;
  sum: number;
  critical: string;
}

const CriticalTableStep: React.FC<CriticalTableStepProps> = ({
  outputs,
  criticalTableData,
  setCriticalTableData,
  isCriticalTableFilled,
}) => {
  const { instance } = useMsal();
  const [ratings, setRatings] = useState<Rating[]>(
    outputs.map(() => ({
      reputational_external_impact_on: 0,
      stakeholders: 0,
      compliance_or_external_regulatory_affairs_impact: 0,
      peopleRoles: 0,
      sum: 0,
    })),
  );

  const [completedRows, setCompletedRows] = useState<ApiResponse[]>([]);
  const [apiResponses, setApiResponses] = useState<(ApiResponse | null)[]>(
    outputs.map(() => null),
  );

  useEffect(() => {
    // When outputs change, reset or update the table data
    const newRatings = outputs.map((output, index) => {
      // Try to find existing rating for this output
      const existingRating = criticalTableData.find(
        (item) => item.output === output,
      );
      const sum = existingRating
        ? (existingRating.reputational_external_impact_on || 0) +
          (existingRating.monthly_financial_impact || 0) +
          (existingRating.compliance_or_external_regulatory_affairs_impact ||
            0) +
          (existingRating.time_to_deliver_the_service || 0)
        : 0;
      return existingRating
        ? {
            reputational_external_impact_on:
              existingRating.reputational_external_impact_on || 0,
            stakeholders: existingRating.monthly_financial_impact || 0,
            compliance_or_external_regulatory_affairs_impact:
              existingRating.compliance_or_external_regulatory_affairs_impact ||
              0,
            peopleRoles: existingRating.time_to_deliver_the_service || 0,
            sum: sum || 0,
          }
        : {
            reputational_external_impact_on: 0,
            stakeholders: 0,
            compliance_or_external_regulatory_affairs_impact: 0,
            peopleRoles: 0,
            sum: 0,
          };
    });

    setRatings(newRatings);

    // Update criticalTableData to match current outputs
    const updatedCriticalData = outputs.map((output) => {
      return (
        criticalTableData.find((item) => item.output === output) || {
          output: output,
          reputational_external_impact_on: 0,
          monthly_financial_impact: 0,
          compliance_or_external_regulatory_affairs_impact: 0,
          time_to_deliver_the_service: 0,
          sum: 0,
          critical: "-",
        }
      );
    });

    setCriticalTableData(updatedCriticalData);

    // Also update apiResponses
    const updatedApiResponses = outputs.map((output) => {
      return criticalTableData.find((item) => item.output === output) || null;
    });
    setApiResponses(updatedApiResponses);
  }, [outputs]); // This effect runs whenever outputs change

  const handleRatingChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
    field: keyof Rating,
    index: number,
  ) => {
    console.log("Field:", field);
    const value = parseInt(e.target.value, 10) || 0;

    setRatings((prevRatings) => {
      const updatedRatings = [...prevRatings];
      const temp = updatedRatings[index];
      temp[field] = value;

      updatedRatings[index] = {
        ...updatedRatings[index],
        [field]: value,
        sum:
          temp.reputational_external_impact_on +
          temp.stakeholders +
          temp.compliance_or_external_regulatory_affairs_impact +
          temp.peopleRoles,
      };

      const isRowComplete = Object.values(updatedRatings[index]).every(
        (val) => val > 0,
      );

      if (isRowComplete) {
        const currentOutput = outputs[index];

        const updatedRow: ApiResponse = {
          output: currentOutput,
          reputational_external_impact_on:
            updatedRatings[index].reputational_external_impact_on,
          monthly_financial_impact: updatedRatings[index].stakeholders,
          compliance_or_external_regulatory_affairs_impact:
            updatedRatings[index]
              .compliance_or_external_regulatory_affairs_impact,
          time_to_deliver_the_service: updatedRatings[index].peopleRoles,
          sum: updatedRatings[index].sum,
          critical: "",
        };

        var newData: any = [];
        const existingIndex = criticalTableData.findIndex(
          (item) => item.output === currentOutput,
        );
        if (existingIndex !== -1) {
          // Update existing row
          newData = [...criticalTableData];
          newData[existingIndex] = updatedRow;
        } else {
          // Insert new row
          newData = [...criticalTableData, updatedRow];
        }
        setCriticalTableData(newData);

        submitRowsData(newData as ApiResponse[]);
      }

      return updatedRatings;
    });
  };

  const submitRowsData = async (rowsToSubmit: ApiResponse[]) => {
    try {
      const apiResponse = await apiService(
        "/management/process_critical_matrix",
        "POST",
        rowsToSubmit,
        { "Content-Type": "application/json" },
        instance as PublicClientApplication,
      );

      if (apiResponse.status === "success" && Array.isArray(apiResponse.data)) {
        const apiData = apiResponse.data;

        setApiResponses((prevApiResponses) => {
          const updatedResponses = [...prevApiResponses];

          apiData.forEach((row: ApiResponse) => {
            const rowIndex = outputs.findIndex(
              (output: string) => output === row.output,
            );
            if (rowIndex !== -1) {
              updatedResponses[rowIndex] = row;

              setCriticalTableData((prevData: CRITICALTableData[]) => {
                const newData = [...prevData];
                const existingIndex = newData.findIndex(
                  (item) => item.output === row.output,
                );
                if (existingIndex >= 0) {
                  newData[existingIndex] = row;
                } else {
                  newData.push(row);
                }
                return newData;
              });
            }
          });

          return updatedResponses;
        });
      } else {
        console.error("Unexpected API response format:", apiResponse);
      }
    } catch (error) {
      console.error("Failed to submit Process Critical Matrix:", error);
      throw error;
    }
  };

  return (
    <>
      <Box>
        <div className={Styles.criticalTableHead}>
          <p>SERVICES / OUTPUTS / DELIVERABLES</p>
          <p>
            Reputational/ External impact on [customers/vendors/employee
            relations/shareholders/auditors] {" "}
          </p>
          <p>
            Monthly Financial Impact – Transactional Value (e.g., Total
            overdue/orders/receivables/Overheads or MJEs) divided by P&L of the
            country (e.g. Net Revenue /EBITDA /Cashflow / ZBB of the
            country) {" "}
          </p>
          <p>Compliance or External regulatory affairs impact</p>
          <p>Time to deliver the service</p>
          <p>Sum</p>
          <p>Critical</p>
        </div>

        {outputs.map((output, index) => {
          const response =
            apiResponses[index] ||
            criticalTableData.find((item) => item.output === output);
          const currentData = criticalTableData.find(
            (item) => item.output === output,
          ) || {
            output: output,
            reputational_external_impact_on: 0,
            monthly_financial_impact: 0,
            compliance_or_external_regulatory_affairs_impact: 0,
            time_to_deliver_the_service: 0,
            sum: 0,
            critical: "-",
          };

          return (
            <div key={index} className={Styles.criticalTableList}>
              <p>{output}</p>
              <select
                value={ratings[index].reputational_external_impact_on}
                onChange={(e) =>
                  handleRatingChange(
                    e,
                    "reputational_external_impact_on",
                    index,
                  )
                }
              >
                <option value="0">-</option>
                <option value="1">Low Impact - 1</option>
                <option value="3">Medium Impact - 3</option>
                <option value="5">High Impact - 5</option>
              </select>
              <select
                value={ratings[index].stakeholders}
                onChange={(e) => handleRatingChange(e, "stakeholders", index)}
              >
                <option value="0">-</option>
                <option value="1">Low Impact - 1</option>
                <option value="3">Medium Impact - 3</option>
                <option value="5">High Impact - 5</option>
              </select>
              <select
                value={
                  ratings[index]
                    .compliance_or_external_regulatory_affairs_impact
                }
                onChange={(e) =>
                  handleRatingChange(
                    e,
                    "compliance_or_external_regulatory_affairs_impact",
                    index,
                  )
                }
              >
                <option value="0">-</option>
                <option value="1">Low Impact - 1</option>
                <option value="3">Medium Impact - 3</option>
                <option value="5">High Impact - 5</option>
              </select>
              <select
                value={ratings[index].peopleRoles}
                onChange={(e) => handleRatingChange(e, "peopleRoles", index)}
              >
                <option value="0">-</option>
                <option value="1">Low Impact - 1</option>
                <option value="3">Medium Impact - 3</option>
                <option value="5">High Impact - 5</option>
              </select>
              <p>{ratings[index].sum || "-"}</p>
              <p
                style={{
                  fontWeight: "bold",
                  color: currentData.critical === "Yes" ? "red" : "green",
                }}
              >
                {currentData.critical || "-"}
              </p>
            </div>
          );
        })}
      </Box>
    </>
  );
};

export default CriticalTableStep;
